<script setup lang="ts" generic="T extends { id: Identifier }">
import { computed, ref } from 'vue';

import ThemedButton from '@/components/base/ThemedButton.vue';
import { ButtonClickedPayload, sendButtonClicked } from '@/utils/analytics/buttonClicked';
import { isDefined } from '@/utils/isDefined';

export type Identifier = string | number;
export type Answer = { answerId: Identifier; resultMapping?: string };
export interface KeyedAnswers {
  [questionId: Identifier]: Answer[];
}

const { pageSection, questions } = defineProps<{
  hideBackButtonFirstQuestion?: boolean;
  pageSection: ButtonClickedPayload['pageSection'];
  questions: T[];
}>();

const emit = defineEmits<{
  back: [];
  'quiz-completed': [results: KeyedAnswers];
}>();

const currentQuestionIndex = ref(0);
const currentQuestion = computed(() => questions[currentQuestionIndex.value]);
const currentQuestionId = computed(() => currentQuestion.value.id);
const selectedAnswers = ref<{
  [questionId: Identifier]: Answer[];
}>({});

function handleAnswerSelected({
  answerId,
  maxSelections,
  questionId,
  resultMapping,
}: {
  answerId: Identifier;
  maxSelections?: number;
  questionId: Identifier;
  resultMapping?: string;
}) {
  if (!selectedAnswers.value[questionId]) {
    selectedAnswers.value[questionId] = [];
  }

  const currentSelection = selectedAnswers.value[questionId];
  const isSelected = currentSelection.find((answer) => answer.answerId === answerId);

  if (isSelected) {
    selectedAnswers.value[questionId] = currentSelection.filter(
      (selection) => selection.answerId !== answerId,
    );
  } else if (maxSelections === 1) {
    selectedAnswers.value[questionId] = [{ answerId, resultMapping }];
  } else if (!isDefined(maxSelections) || currentSelection.length < maxSelections) {
    selectedAnswers.value[questionId] = [...currentSelection, { answerId, resultMapping }];
  }
}

const isFirstQuestion = computed(() => currentQuestion.value === questions[0]);
const isLastQuestion = computed(() => currentQuestion.value === questions.at(-1));

const isCurrentQuestionAnswered = computed(() => {
  const selections = selectedAnswers.value[currentQuestionId.value] || [];
  return selections.length > 0;
});

const handleNext = () => {
  sendButtonClicked({
    buttonName: `Question ${currentQuestionIndex.value + 1} Next button`,
    content: 'Next',
    pageSection,
  });

  if (isLastQuestion.value) {
    emit('quiz-completed', selectedAnswers.value);
  } else {
    currentQuestionIndex.value += 1;
  }
};

const handleBack = () => {
  sendButtonClicked({
    buttonName: `Question ${currentQuestionIndex.value + 1} Back button`,
    content: 'Back',
    pageSection,
  });

  if (isFirstQuestion.value) {
    emit('back');
  } else {
    currentQuestionIndex.value -= 1;
  }
};

const gridClassesByColumn = (columns: number) => {
  switch (columns) {
    case 1:
      return 'grid-cols-1';
    case 2:
      return 'grid-cols-2';
    case 3:
      return 'grid-cols-3';
    case 4:
      return 'grid-cols-4';
    default:
      return 'grid-cols-1';
  }
};
</script>

<template>
  <div class="relative flex flex-col items-center">
    <div class="grid w-full gap-4 px-4 mb-6" :class="gridClassesByColumn(questions.length)">
      <div
        v-for="i in questions.length"
        :key="i"
        class="w-full rounded-full h-1.5"
        :class="[i <= currentQuestionIndex + 1 ? 'bg-neutral-700' : 'bg-neutral-200']"
      />
    </div>

    <div class="w-full px-4">
      <slot
        v-bind="{
          currentQuestion,
          currentQuestionIndex,
          handleAnswerSelected,
          selectedAnswers,
        }"
      />
    </div>

    <div
      class="fixed bottom-0 left-0 right-0 flex p-4 mx-auto bg-white border-t border-solid sm:absolute border-neutral-100"
      :class="isFirstQuestion && hideBackButtonFirstQuestion ? 'justify-end' : 'justify-between'"
    >
      <ThemedButton
        v-if="!(isFirstQuestion && hideBackButtonFirstQuestion)"
        size="large"
        theme="white"
        @click="handleBack"
      >
        Back
      </ThemedButton>

      <ThemedButton
        :class="isFirstQuestion && hideBackButtonFirstQuestion ? 'w-full sm:w-auto' : ''"
        size="large"
        theme="gray"
        :disabled="!isCurrentQuestionAnswered"
        @click="handleNext"
      >
        Next
      </ThemedButton>
    </div>
  </div>
</template>
